import { Box, Typography } from '@mui/material';
import { clientDayKey } from '../../shared/dateTime';
import { plural } from '../../shared/plural';
import { useFeature } from '../featureFlags/FeatureGate';
import { useCarCard } from './CarCardContext';
import { OCCUPIED_UNTIL_TMS_PLAN, VEHICLE_VARIANTS_LANE } from './fleetFeatures';
import { viewWindow } from './ganttRules';
import { VehicleGantt } from './VehicleGantt';
import { withoutTmsPlan } from './vehiclePlanApi';

/**
 * Pasek osi nad „Rekomendowanymi zleceniami” (15.10.2026, przegląd Etapu 1
 * w przeglądarce, przełącznik `vehiclePreviewSegments`).
 *
 * Przy 1366×768 oś harmonogramu stoi ok. 1100 px nad tabelą rekomendacji —
 * podgląd zlecenia po najechaniu na wiersz rysował się poza ekranem.
 * Pasek przykleja się u góry ekranu (`position: sticky`), dopóki planista
 * przewija sekcję rekomendacji, i pokazuje to, czego podgląd potrzebuje:
 * wiersze „Zaplanowane” (i „Warianty”) od dziś do końca horyzontu
 * rekomendacji, z podglądem najechanego zlecenia. Bez aktywności kierowcy,
 * toolbaru i legendy — ok. 120–170 px, więc przy 768 px wysokości zostaje
 * miejsce na kilka wierszy tabeli.
 *
 * Plan auta to ten sam odczyt co pasek podsumowania (`overview` —
 * domyślne okno API: tydzień wstecz, trzy naprzód), więc pasek nie pyta API
 * drugi raz i nie zależy od tego, gdzie planista przewinął główną oś.
 */
export const SuggestionsAxis = ({ horizonDays, now }: { horizonDays: number; now?: Date }) => {
  const { overview, preview } = useCarCard();
  const variantsLane = useFeature(VEHICLE_VARIANTS_LANE);
  // „Zajęte do” wg planu TMS tylko z przełącznikiem — jak na głównej osi.
  const tmsPlanOn = useFeature(OCCUPIED_UNTIL_TMS_PLAN);
  const plan = overview.plan && !tmsPlanOn ? withoutTmsPlan(overview.plan) : overview.plan;
  if (!plan) return null;
  const days = axisDays(horizonDays);
  const window = viewWindow(clientDayKey(now ?? new Date()), days);
  return (
    <Box
      data-testid="suggestions-axis"
      sx={{
        position: 'sticky',
        // Pod paskiem aplikacji (48 px): przy przewijaniu w górę pasek się
        // wysuwa i zasłaniałby podpis i dni osi (decyzja 15.10.2026 — daty
        // zawsze widoczne, kosztem szczeliny, gdy pasek jest schowany).
        top: 48,
        // Nad tabelą przy przewijaniu, pod paskiem aplikacji i dymkami.
        zIndex: 5,
        bgcolor: 'background.paper',
        pt: 0.5,
        pb: 1,
        mb: 1,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {`Oś pojazdu od dziś (${plural(days, 'dzień', 'dni', 'dni')}): najedź na zlecenie w tabeli, a stanie tu jako podgląd (szacunek). Pełny harmonogram jest wyżej.`}
      </Typography>
      <VehicleGantt
        compact
        window={window}
        days={days}
        detail="simple"
        items={plan.items}
        months={plan.months}
        driverWeeks={[]}
        activity={null}
        now={now}
        highlight={null}
        onHighlight={noop}
        preview={preview}
        variantsLane={variantsLane}
      />
    </Box>
  );
};

const noop = () => undefined;

/**
 * Dni paska: horyzont rekomendacji i dzień dzisiejszy (załadunek „w ciągu
 * 7 dni” kończy się ósmego dnia kalendarza), nie mniej niż 3 i nie więcej
 * niż 21 — dalej domyślne okno planu z API (trzy tygodnie naprzód) nie
 * sięga, a dni robią się za wąskie na podpis.
 */
export function axisDays(horizonDays: number): number {
  const n = Number.isFinite(horizonDays) ? Math.round(horizonDays) + 1 : 8;
  return Math.min(21, Math.max(3, n));
}
